import React from "react";
import Paper from "@mui/material/Paper";
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FullTextField from "../commonControls/FullTextField";
import { useState, useEffect } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as yup from "yup";
import { loginRoute, meRoute, getAllClientsRoute, getAllProjectsRoute } from "../../routes/apiRoutes";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import {
  loginRoute as appLoginRoute,
  forgotPasswordRoute,
  dashboardRoute,
  selectPlanRoute,
} from "../../routes/reactRoutes";
import { useSelector, useDispatch } from "react-redux";
import { login } from "../../Redux/authenticationSlice";
import { apply } from "../../Redux/themeSlice";
import { LogoAndText } from "../../theme/JaziTheme";
import { Buffer } from "buffer";
import LoadingButton from "@mui/lab/LoadingButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axiosInstance from "../../axios/axiosInstance";
import apiService from "../../services/apiService";
import { t } from "i18next";
import FlipLanguageSwitcher from "./FlipLanguageSwitcher";
import { setClientProject } from "../../Redux/clientProjectSlice";

const Login = (props) => {
  const theme = useTheme();
  const colors = useSelector((state) => state.themeValues.colors);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const prevLocation = localStorage.getItem("prevLocation");
  const navLocation =
    prevLocation && prevLocation !== appLoginRoute
      ? prevLocation
      : dashboardRoute;

  useEffect(() => {
    document.title = props.title;
    if (localStorage.getItem("token")) handleToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Object.keys(formik.touched).forEach((field) => {
      try {
        formik.validateField(field);
      } catch {}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.direction]);

  const handleToken = async () => {
    try {
      setLoading(true);
      const data = await apiService.get(meRoute);
        dispatch(
          login({
            user: data,
            token: null,
            avatar: localStorage.getItem("avatar"),
            subscriptionStatus: data.response.subscriptionStatus,
          })
        );
        if (data?.theme) {
          dispatch(
            apply({
              colors: colors,
              mainColor: data.response.theme.mainColor,
              mode: data.response.theme.mode,
            })
          );
        }
        navigate(navLocation,{replace: true});
      }
    catch (error) {
      console.log("error", error);
      //
    } finally {
      setLoading(false);
    }
  };
  const getImageBase64 = async (imageUrl) => {
    try {
      const response = await axiosInstance.get(imageUrl, {
        responseType: "arraybuffer",
      });
      const base64Data = Buffer.from(response.data).toString("base64");
      return `data:${response.headers["content-type"]};base64,${base64Data}`;
    } catch {}
  };

  const schema = yup.object({
    password: yup.string().trim().required(t("passwordisrequired")),
    email: yup
      .string()
      .trim()
      .required(t("emailisrequired"))
      .email(t("emailinvalid")),
  });
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const data = await apiService.post(loginRoute, {
          email: values.email?.trim(),
          password: values.password?.trim(),
        });
        
        let avatar = null;
        if (data.signedPreviewUrl) {
          avatar = await getImageBase64(data.signedPreviewUrl.url);
        }
        setLoading(false);
        console.log("login data", data);
        dispatch(
          login({
            user: data.user,
            token: data.accessToken,
            avatar: avatar,
            subscriptionStatus: data.user?.subscriptionStatus,
          })
        );
        if (data?.user?.theme) {
          dispatch(
            apply({
              colors: colors,
              mainColor: data.user.theme.mainColor,
              mode: data.user.theme.mode,
            })
          );
        }
        try {
          const [clientsRes, projectsRes] = await Promise.all([
            apiService.get(`${getAllClientsRoute}?take=5&&pageNo=1&&searchText=`),
            apiService.get(`${getAllProjectsRoute}?take=5&&pageNo=1&&searchText=`),
          ]);
          dispatch(
            setClientProject({
              clients: clientsRes?.clients || clientsRes || [],
              projects: projectsRes?.projects || projectsRes || [],
            })
          );
        } catch {}
        toast.dismiss();
        if (data.user?.subscriptionStatus) {
          navigate(navLocation,{replace: true});
        } else {
          navigate(selectPlanRoute,{replace: true});
        }
      } catch (error) {
        // Only handle login-specific errors here (400 for bad credentials)
        if (error.response?.data?.statusCode === 400) {
          toast.dismiss();
          if(error.response?.data?.message === "You are not authorized to access this app, please login on mobile app") {
            toast.error(t("invalidcredentials"), {
              autoClose: false,
            });
          } else {
            toast.error(t("logincredentialerror"), {
              autoClose: false,
            });
          }
        }else if (error.response?.data?.statusCode === 404) {
          toast.dismiss();
          toast.error(error.response?.data?.message, {
            autoClose: false,
          });
        } else {
          // Only show if interceptor didn't already show something
          if (error.message !== "Network Error") {
            toast.dismiss();
            toast.error(t("loginerror"), {
              autoClose: false,
            });
          }
        }

        formik.resetForm();
        setLoading(false);
      }
    },
  });

  const handlePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const styles = {
    outerBox: {
      background: theme.palette.whiteblack.main,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      //border: `0.5px solid ${theme.palette.componentBackgrounds.main}`,
      borderRadius: 1,
      boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
      py: 5,
      px: 2,
      maxWidth: 490,
      maxHeight: 450,
      width: "100%",
      height: "100vh",
    },
    formStyle: {
      display: "flex",
      justifyContent: "space-between",
      height: "100%",
      flexDirection: "column",
      alignItems: "center",
      minWidth: "90%",
    },
    rootStyle: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      margin: 0,
      background: theme.palette.primary.main,
      backgroundImage: "url('background.webp')",
      backgroundSize: "cover",
    },
    textFieldStyle: {
      background: theme.palette.componentBackgrounds.new,
      width: "100%",
      my: 1.75,
      border: "none",
      borderRadius: 1,
      height: 38,
    },
  };

  return (
    <div dir={theme.direction} style={styles.rootStyle}>
      <Paper elevation={0} sx={styles.outerBox}>
        <Box
          sx={{ display: "flex", justifyContent: "center", mb: 2, zoom: 0.8 }}
        >
          <LogoAndText />
        </Box>
        <div
          style={{
            display: "flex",
            width: "90%",
            alignItems: "center",
            textAlign: "left",
            marginBottom: theme.spacing(3),
          }}
          dir={theme.direction}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 800,
              textAlign: "left",
              alignSelf: "center",
              flexGrow: 1,
            }}
          >
            {t("signin")}
          </Typography>
          <FlipLanguageSwitcher />
        </div>

        <form style={styles.formStyle} onSubmit={formik.handleSubmit}>
          <Typography
            sx={{ textAlign: "left", width: "100%", fontWeight: 600 }}
          >
            {`${t("emailAddress")}*`}
          </Typography>
          <FullTextField
            id="profile-email-field"
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="email"
            type="text"
            label={t("enterYourEmail")}
          />
          <Typography
            sx={{ textAlign: "left", width: "100%", fontWeight: 600, mt: 1 }}
          >
            {`${t("password")}*`}
          </Typography>
          <TextField
            id="login-password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            name="password"
            type={!showPassword ? "password" : "text"}
            placeholder={t("enterYourPassword")}
            size="small"
            sx={styles.textFieldStyle}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handlePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <LoadingButton
            type="submit"
            loading={loading}
            variant="contained"
            sx={{
              width: "100%",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              my: 2.5,
              py: 1.5,
            }}
          >
            <span>{t("signin")}</span>
          </LoadingButton>
        </form>
        <Link
          to={forgotPasswordRoute}
          style={{
            fontSize: "0.9375rem",
            fontWeight: 700,
            color: theme.palette.whiteblack.contrastText,
          }}
        >
          {t("forgotPassword?")}
        </Link>
      </Paper>
    </div>
  );
};

export default Login;
